/**
 * groupBasketPayment: paying an Order together share, the host's remainder,
 * or the host's one order, on the EXISTING web payment rails
 * (group-baskets-contract.md, section 6).
 *
 * A share is paid exactly like an order: the contribution's `orderId` is the
 * order, its `amount` is the order's total and the signed-in payer is its
 * `clientId`. The pre-charge guard refuses any amount but the order's own, so
 * the amount is read through orderChargeAmount (redux/checkout) and sent
 * unchanged. Nothing here adds up money.
 *
 *   MTN, Uganda                 POST /momo/:clientId         (payMoMoCheckout)
 *   Airtel UG, all ZM mobile    POST /fw/flutterAirtelPayment (payAirtelMoneyCheckout)
 *   Card                        POST /fw/flutterCardPayment   (payCardCheckout)
 *
 * Mobile money from Flutterwave and card 3-D Secure come back to the basket
 * through `redirect_url`; a card PIN or OTP ends on /cardOrder, which sends
 * the shopper back here (utils/cardPayment attachCardReturnPath).
 */
import { PAYMENT_METHODS, paymentMethodFor } from "../config/market";
import { orderChargeAmount } from "../redux/checkout";
import {
  payAirtelMoneyCheckout,
  payCardCheckout,
  payMoMoCheckout,
} from "../redux/createOrderAction";
import { attachCardReturnPath, followCardRedirect } from "./cardPayment";
import { validatePaymentPhone } from "./paymentPhone";

export const RAIL = Object.freeze({
  MTN_DIRECT: "MTN_DIRECT",
  FW_MOMO: "FW_MOMO",
  CARD: "CARD",
  COD: "COD",
});

/*
 * The wire values the group endpoints use, to the market registry's keys.
 * Contributions list `AIRTEL` and `CARD`; checkout takes `Airtel` and `CARD`.
 */
const REGISTRY_KEY_BY_WIRE = {
  MOMO: "MOMO",
  AIRTEL: "Airtel",
  ZAMTEL: "ZAMTEL",
  CARD: "Visa",
  VISA: "Visa",
  COD: "COD",
};

const CHECKOUT_WIRE_BY_KEY = {
  MOMO: "MOMO",
  Airtel: "Airtel",
  ZAMTEL: "ZAMTEL",
  Visa: "CARD",
  COD: "COD",
};

const optionFor = (method, entry) => ({
  method,
  key: entry.key,
  label: entry.label,
  imageUrl: entry.imageUrl || "",
  rail: entry.rail,
  network: entry.network || null,
  needs: entry.rail === RAIL.CARD ? "card" : entry.rail === RAIL.COD ? null : "phone",
  analyticsRail: String(method).toLowerCase(),
});

/* A contribution's `allowedMethods` as this market's payment options. */
export const contributionPayOptions = (allowedMethods) =>
  (Array.isArray(allowedMethods) ? allowedMethods : [])
    .map((method) => {
      const entry = paymentMethodFor(REGISTRY_KEY_BY_WIRE[String(method).toUpperCase()]);
      return entry && entry.rail !== RAIL.COD ? optionFor(method, entry) : null;
    })
    .filter(Boolean);

/* HOST_PAYS_ALL: every method this market's order path takes, COD included. */
export const hostCheckoutPayOptions = () =>
  PAYMENT_METHODS.filter((entry) => CHECKOUT_WIRE_BY_KEY[entry.key]).map((entry) =>
    optionFor(CHECKOUT_WIRE_BY_KEY[entry.key], entry)
  );

/* The web card form's values as the card endpoint takes them. */
export const cardDetailsFromForm = (data = {}) => {
  const [expiry_month = "", expiry_year = ""] =
    typeof data.expirationDate === "string" ? data.expirationDate.split("/") : [];
  return {
    cardNumber: String(data.cardNumber || "").replace(/\s/g, ""),
    expiry_month: expiry_month.trim(),
    expiry_year: expiry_year.trim(),
    cardCvv: String(data.cvv || ""),
    cardType: data.cardType || "",
    fullName: String(data.fullName || "").trim(),
  };
};

export const cardDetailsComplete = (details) =>
  Boolean(
    details &&
      details.cardNumber.length >= 12 &&
      details.expiry_month &&
      details.expiry_year &&
      details.cardCvv.length >= 3 &&
      details.fullName
  );

export const PAYMENT_AMOUNT_UNREADABLE =
  "We could not read the amount to pay, so nothing has been charged. Refresh the basket and try again.";

/**
 * The one charge request for an order on a rail, exactly as section 6 lists
 * it. Pure, so the hand-off can be tested without a network.
 *
 * @param {object} args
 * @param {object} args.option     one of contributionPayOptions / hostCheckoutPayOptions
 * @param {string} args.orderId    the contribution's (or checkout's) order
 * @param {number|object} args.amount  the server's amount, or an order with totalPrice
 * @param {string} args.clientId   the signed-in payer
 * @param {string} [args.mobile]   mobile money number
 * @param {object} [args.cardDetails]
 * @param {string} [args.returnUrl] where Flutterwave sends the shopper back
 * @param {string} [args.email]
 * @returns {{ ok: true, rail, body } | { ok: false, message }}
 */
export const chargeRequestFor = ({
  option,
  orderId,
  amount,
  clientId,
  mobile,
  cardDetails,
  returnUrl,
  email,
}) => {
  const totalPrice = orderChargeAmount(amount);
  if (!option || option.rail === RAIL.COD) return { ok: false, message: "Choose how to pay." };
  if (!orderId || !clientId) return { ok: false, message: PAYMENT_AMOUNT_UNREADABLE };
  if (totalPrice === null) return { ok: false, message: PAYMENT_AMOUNT_UNREADABLE };
  if (option.needs === "phone") {
    const phone = validatePaymentPhone(mobile, option.key);
    if (!phone.ok) return { ok: false, message: phone.message };
    mobile = phone.local;
  }
  const withEmail = email ? { email } : {};
  if (option.rail === RAIL.MTN_DIRECT) {
    return {
      ok: true,
      rail: option.rail,
      body: { mobile, totalPrice, client_id: clientId, orderId },
    };
  }
  if (option.rail === RAIL.FW_MOMO) {
    return {
      ok: true,
      rail: option.rail,
      body: {
        mobile,
        totalPrice,
        client_id: clientId,
        network: option.network,
        redirect_url: returnUrl,
        orderId,
        ...withEmail,
      },
    };
  }
  return {
    ok: true,
    rail: RAIL.CARD,
    body: {
      cardDetails,
      totalPrice,
      client_id: clientId,
      redirect_url: returnUrl,
      orderId,
      ...withEmail,
    },
  };
};

const rejectionMessage = (action, fallback) => {
  const payload = action?.payload;
  if (typeof payload === "string" && payload) return payload;
  return (
    payload?.response?.data?.message ||
    payload?.response?.data?.msg ||
    payload?.message ||
    fallback
  );
};

const NOT_STARTED =
  "We could not start the payment. Nothing has been charged. Please try again.";

/**
 * Start a charge on the existing thunks. Resolves to
 * { ok, redirecting, message, toasted }: `toasted` when the thunk already said
 * why (the Airtel and card thunks toast their own refusals).
 *
 * @param {object} args
 * @param {Function} args.dispatch
 * @param {object} args.request   chargeRequestFor's answer
 * @param {string} [args.returnPath] the basket's path, for a card PIN or OTP
 */
export const startGroupCharge = async ({ dispatch, request, returnPath }) => {
  if (!request?.ok) return { ok: false, message: request?.message || NOT_STARTED };

  if (request.rail === RAIL.MTN_DIRECT) {
    const action = await dispatch(payMoMoCheckout(request.body));
    if (action?.error) return { ok: false, message: rejectionMessage(action, NOT_STARTED) };
    return { ok: true, redirecting: false };
  }

  if (request.rail === RAIL.FW_MOMO) {
    const action = await dispatch(payAirtelMoneyCheckout(request.body));
    if (action?.error) return { ok: false, message: rejectionMessage(action, NOT_STARTED), toasted: true };
    const redirectUrl =
      action?.payload?.redirectUrl || action?.payload?.data?.meta?.authorization?.redirect;
    if (!redirectUrl) return { ok: false, message: NOT_STARTED };
    followCardRedirect(redirectUrl);
    return { ok: true, redirecting: true };
  }

  // The thunk stashes the card context before its request; the return path
  // joins it at once, before any PIN or OTP page can read it.
  const pending = dispatch(payCardCheckout(request.body));
  if (returnPath) attachCardReturnPath(returnPath);
  const action = await pending;
  if (action?.error) return { ok: false, message: rejectionMessage(action, NOT_STARTED), toasted: true };
  if (action?.payload?.redirectUrl) {
    followCardRedirect(action.payload.redirectUrl);
    return { ok: true, redirecting: true };
  }
  return { ok: true, redirecting: false };
};

/* ── The payment this browser started, until the basket settles it ─────── */

const PAYMENT_STORAGE_KEY = "lusuku.groupPayment";
const PAYMENT_RECORD_TTL_MS = 30 * 60 * 1000;

export const rememberGroupPayment = (record, now = Date.now()) => {
  try {
    sessionStorage.setItem(PAYMENT_STORAGE_KEY, JSON.stringify({ ...record, startedAt: now }));
  } catch (_) {
    /* storage disabled: the basket's own status still decides */
  }
};

export const groupPaymentFor = (basketId, now = Date.now()) => {
  try {
    const record = JSON.parse(sessionStorage.getItem(PAYMENT_STORAGE_KEY) || "null");
    if (!record || String(record.basketId) !== String(basketId)) return null;
    if (!(now - Number(record.startedAt) < PAYMENT_RECORD_TTL_MS)) return null;
    return record;
  } catch (_) {
    return null;
  }
};

export const clearGroupPayment = () => {
  try {
    sessionStorage.removeItem(PAYMENT_STORAGE_KEY);
  } catch (_) {
    /* nothing to clear */
  }
};

/* The status read for a payment in flight (section 6: the rail's own poll). */
export const paymentStatusPath = (record) =>
  record?.rail === RAIL.MTN_DIRECT
    ? `/momo/${encodeURIComponent(record.clientId)}?orderId=${encodeURIComponent(record.orderId)}`
    : `/fw/payment-status/${encodeURIComponent(record?.orderId || "")}`;

/* "successful" | "failed" | "pending", from either rail's poll answer. */
export const paymentPollOutcome = (record, data) => {
  if (record?.rail === RAIL.MTN_DIRECT) {
    if (data?.msg === "Transaction Successful") return "successful";
    if (data?.msg === "Transaction Failed") return "failed";
    return "pending";
  }
  if (data?.status === "successful") return "successful";
  if (data?.status === "failed") return "failed";
  return "pending";
};
